/**
 * showdownMoves.batch2 — the second batch of per-move Showdown animation scripts
 * (the "d…f" alphabetic slice: dragon-* through fling).
 *
 * Each entry is a near-verbatim port of the move's script from Pokémon Showdown's
 * `BattleMoveAnims` (`battle-animations-moves.ts`, MIT/CC0). Our {@link FxScene} +
 * {@link Actor} mirror Showdown's `scene` + `Sprite`, so the literal
 * `scene.showEffect(...)` / `attacker.anim(...)` / `scene.wait(...)` sequences
 * port unchanged. Where a move points at a shared `BattleOtherAnims` template that
 * the engine already exports (contactattack, slashattack, punchattack, bite), we
 * reuse it from {@link module:showdownOther}; where the template is NOT yet
 * exported there (clawattack, shake, xattack, sound, dance, selfstatus,
 * sneakattack, flight) we port that template inline below (this batch owns only
 * this file, so it cannot extend showdownOther.ts).
 *
 * Effect-name discipline: only effect keys vendored in our fx catalog are used.
 * Adaptations, all matching conventions already established in showdownMoves.ts:
 *   - `scene.showEffect(attacker.sp, …)` (the attacker's own sprite drawn as a
 *     streak/after-image) has no equivalent in our engine — those calls are
 *     OMITTED (same as `fastattack`/`fastanimattack` in showdownOther.ts).
 *   - `scene.$bg.animate(...)` (a raw background pan, used by earth-power) has no
 *     equivalent — substituted with a short brown `backgroundEffect` ground-rumble
 *     wash (same substitution earthquake uses).
 *   - `backgroundEffect` colours given as CSS `linear-gradient(...)` would render
 *     as a flat white wash through our parser; they are substituted with a flat hex
 *     approximation of the gradient's dominant colour, noted inline.
 *
 * Test/runtime separation: imports ONLY TYPES from `showdownFx.ts` plus the pure
 * template functions from `showdownOther.ts`; every script is a pure function over
 * the `FxScene`/`Actor` interfaces, unit-testable in node without Phaser.
 */
import type { FxScene, Actor, MoveAnim } from './showdownFx.js';
import { contactattack, slashattack, punchattack, bite } from './showdownOther.js';

/* ──────────────────────────────────────────────────────────────────────────
 * Inline template ports — `BattleOtherAnims` entries this batch needs that are
 * not (yet) exported by showdownOther.ts. Ported near-verbatim from
 * `battle-animations.ts` (MIT/CC0). Local to this file.
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `BattleOtherAnims.clawattack` — a slashing X of claws + a double lunge. */
const clawattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(450);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');

  scene.showEffect(
    'leftclaw',
    { x: defender.x - 20, y: defender.y + 20, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.x - 20, y: defender.y + 20, z: defender.z, scale: 3, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'leftclaw',
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 0, opacity: 1, time: 400 },
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 3, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightclaw',
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 0, opacity: 1, time: 700 },
    { x: defender.x + 20, y: defender.y + 20, z: defender.z, scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rightclaw',
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 0, opacity: 1, time: 700 },
    { x: defender.x + 20, y: defender.y - 20, z: defender.z, scale: 3, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
};

/** PS `BattleOtherAnims.shake` — a slow side-to-side shake of the user. */
const shake: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10, time: 200 });
  attacker.anim({ x: attacker.x + 10, time: 300 });
  attacker.anim({ x: attacker.x, time: 200 });
};

/** PS `BattleOtherAnims.xattack` — a crossing double-lunge with two wisp bursts. */
const xattack: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 400 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 700,
    },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 700 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 3,
      opacity: 0,
      time: 1000,
    },
    'linear',
  );
  defender.delay(480);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 200 }, 'swing');
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
  attacker.anim(
    { x: defender.leftof(-30), y: defender.y + 80, z: defender.behind(-30), time: 400 },
    'ballistic',
  );
  attacker.anim({ x: defender.leftof(30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim(
    { x: defender.leftof(30), y: defender.y + 80, z: defender.behind(-30), time: 200 },
    'ballisticUp',
  );
  attacker.anim({ x: defender.leftof(-30), y: defender.y + 5, z: defender.z, time: 100 });
  attacker.anim({ time: 500 }, 'ballistic2Back');
};

/** PS `BattleOtherAnims.sound` — three concentric wisp rings ripple off the user. */
const sound: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 0 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 150 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 300 },
    { z: attacker.behind(-50), scale: 5, opacity: 0, time: 800 },
    'linear',
  );
};

/** PS `BattleOtherAnims.dance` — a quick side-to-side sway of the user. */
const dance: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10 });
  attacker.anim({ x: attacker.x + 10 });
  attacker.anim({ x: attacker.x });
};

/** PS `BattleOtherAnims.selfstatus` — two wisps collapse inward on the user. */
const selfstatus: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 0 },
    { scale: 0, opacity: 1, time: 300 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 2, opacity: 0.2, time: 200 },
    { scale: 0, opacity: 1, time: 500 },
    'linear',
  );
};

/** PS `BattleOtherAnims.sneakattack` — vanish, reappear behind the defender, strike. */
const sneakattack: MoveAnim = (_scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: attacker.leftof(-20), y: attacker.y, z: attacker.behind(-20), opacity: 0, time: 200 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-120), opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(40), opacity: 1, time: 250 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-5), opacity: 0, time: 300 },
    'linear',
  );
  attacker.anim({ opacity: 0, time: 1 }, 'linear');
  attacker.anim({ time: 300, opacity: 1 }, 'linear');
  defender.delay(330);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `BattleOtherAnims.flight` — fly off-screen, swoop back through the defender. */
const flight: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim(
    { x: attacker.leftof(-200), y: attacker.y + 80, z: attacker.z, opacity: 0, time: 350 },
    'accel',
  );
  attacker.anim({ x: defender.leftof(-200), y: defender.y + 80, z: defender.z, time: 1 }, 'linear');
  attacker.anim({ x: defender.x, y: defender.y, z: defender.z, opacity: 1, time: 350 }, 'accel');
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 700 },
    { scale: 2, opacity: 0, time: 900 },
    'linear',
  );
  attacker.anim({
    x: defender.leftof(100),
    y: defender.y - 40,
    z: defender.z,
    opacity: 0,
    time: 175,
  });
  attacker.anim({ x: attacker.x, y: attacker.y + 40, z: attacker.behind(40), time: 1 });
  attacker.anim({ x: attacker.x, y: attacker.y, z: attacker.z, time: 250 }, 'decel');
  defender.delay(700);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/* ──────────────────────────────────────────────────────────────────────────
 * Per-move scripts (alphabetic slice). One `const` per move; aliases assign a
 * template or another ported move's function (matching PS's alias table).
 * ──────────────────────────────────────────────────────────────────────── */

/** PS `dragonbreath` — three poison wisps stream onto the defender. */
const dragonbreath: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5 },
    { x: defender.x, y: defender.y, z: defender.behind(40), scale: 1, opacity: 0.2 },
    'decel',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 50 },
    { x: defender.x, y: defender.y, z: defender.behind(40), scale: 1, opacity: 0.2 },
    'decel',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0.5, time: 100 },
    { x: defender.x, y: defender.y, z: defender.behind(40), scale: 1, opacity: 0.2 },
    'decel',
  );
};

/** PS `dragoncheer` — the user wells up with energy; the defender focuses. */
const dragoncheer: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // Attacker cheers
  for (let i = 0; i < 3; i++) {
    scene.showEffect(
      'shadowball',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.6, time: 150 * i },
      { z: attacker.behind(-50), scale: 2, opacity: 0, time: 400 + 200 * i },
      'linear',
    );
    scene.showEffect(
      'wisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.7, time: 150 * i },
      { z: attacker.behind(-50), scale: 5, opacity: 0, time: 400 + 200 * i },
      'linear',
    );
    scene.showEffect(
      'poisonwisp',
      { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.5, time: 150 * i },
      { z: attacker.behind(-50), scale: 2, opacity: 0, time: 400 + 200 * i },
      'linear',
    );
  }

  // Defender focuses
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0.2, time: 800 },
    { scale: 0, opacity: 1, time: 1100 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0.2, time: 1000 },
    { scale: 0, opacity: 1, time: 1300 },
    'linear',
  );
};

/** PS `dragonclaw` → `clawattack` template. */
const dragonclaw: MoveAnim = clawattack;

/** PS `dragondance` — a shake plus three poison wisps lobbed up around the user. */
const dragondance: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [attacker] = actors;
  shake(scene, actors);
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 0 },
    { x: attacker.x - 50, y: attacker.y - 50, scale: 2, opacity: 0, time: 300 },
    'ballistic',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 150 },
    { x: attacker.x + 60, y: attacker.y - 50, scale: 2, opacity: 0, time: 450 },
    'ballistic',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 300 },
    { x: attacker.x + 10, y: attacker.y - 60, scale: 2, opacity: 0, time: 600 },
    'ballistic',
  );
};

/** PS `dragonpulse` — a wisp/poison beam, then a barrage of shadow orbs. */
const dragonpulse: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  const xstep = (defender.x - attacker.x) / 5;
  const ystep = (defender.y - attacker.y) / 5;
  const zstep = (defender.z - attacker.z) / 5;

  for (let i = 0; i < 5; i++) {
    scene.showEffect(
      'wisp',
      {
        x: attacker.x + xstep * (i + 1),
        y: attacker.y + ystep * (i + 1),
        z: attacker.z + zstep * (i + 1),
        scale: 1,
        opacity: 1,
        time: 20 * i,
      },
      { scale: 2, opacity: 0, time: 40 * i + 600 },
      'linear',
    );
    scene.showEffect(
      'poisonwisp',
      {
        x: attacker.x + xstep * (i + 1),
        y: attacker.y + ystep * (i + 1),
        z: attacker.z + zstep * (i + 1),
        scale: 0.5,
        opacity: 0.3,
        time: 20 * i,
      },
      { scale: 2, opacity: 0, time: 40 * i + 600 },
      'linear',
    );
  }
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.behind(-15), scale: 0.5, opacity: 0.6 },
    { scale: 0.6, opacity: 0.2, time: 400 },
    'linear',
    'fade',
  );

  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.1, time: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.1, time: 100 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.1, time: 200 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.3, opacity: 0.1, time: 300 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, time: 500 },
    'linear',
    'explode',
  );
};

/** PS `dragontail` → `contactattack` template. */
const dragontail: MoveAnim = contactattack;

/** PS `dragonhammer` → PS aliases this to `heavyslam`; we map to the closest
 * vendored template, `contactattack` (heavyslam is a heavy contact slam; not
 * ported in our set, so the canonical contact lunge stands in). */
const dragonhammer: MoveAnim = contactattack;

/** PS `dragonrage` → alias of `dragonbreath`. */
const dragonrage: MoveAnim = dragonbreath;

/** PS `dragonrush` → PS aliases this to `gigaimpact` (not in our set); the closest
 * vendored stand-in is the canonical contact lunge, `contactattack`. */
const dragonrush: MoveAnim = contactattack;

/** PS `drainpunch` — three electro orbs siphon back, then a fist jab. */
const drainpunch: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [attacker, defender] = actors;
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 600 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 900, opacity: 0 },
    'ballistic2',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 650 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 950, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 1, time: 700 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 1000, opacity: 0 },
    'ballistic2Under',
  );
  punchattack(scene, actors);
};

/** PS `drainingkiss` — three mist orbs siphon defender → attacker. */
const drainingkiss: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 0 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 500, opacity: 0 },
    'ballistic2',
  );
  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 50 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 550, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'mistball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.6, opacity: 0.6, time: 100 },
    { x: attacker.x, y: attacker.y, z: attacker.z, time: 600, opacity: 0 },
    'ballistic2Under',
  );
};

/** PS `dreameater` → alias of `drainingkiss`. */
const dreameater: MoveAnim = drainingkiss;

/** PS `drillpeck` → `contactattack` template. */
const drillpeck: MoveAnim = contactattack;

/** PS `drillrun` — a drilling lunge with wisps, impacts, and spiralling flares. */
const drillrun: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 200 });
  defender.anim({ z: defender.behind(0), time: 200 });

  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 300 },
    { scale: 3, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 400 },
    { scale: 3, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 300 },
    { scale: 2, opacity: 0, time: 500 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.4, time: 500 },
    { scale: 2, opacity: 0, time: 700 },
    'linear',
  );
  scene.showEffect(
    'flareball',
    {
      x: defender.leftof(10),
      y: defender.y - 10,
      z: defender.behind(-40),
      scale: 0.7,
      yscale: 1,
      opacity: 0.8,
      time: 300,
    },
    { z: defender.behind(5), scale: 0, xscale: 0, yscale: 0, time: 450 },
    'accel',
  );
  scene.showEffect(
    'flareball',
    {
      x: defender.leftof(10),
      y: defender.y - 10,
      z: defender.behind(-40),
      scale: 0.7,
      yscale: 1,
      opacity: 0.8,
      time: 350,
    },
    { z: defender.behind(5), scale: 0, xscale: 0, yscale: 0, time: 500 },
    'accel',
  );
  scene.showEffect(
    'flareball',
    {
      x: defender.leftof(10),
      y: defender.y - 10,
      z: defender.behind(-40),
      scale: 0.7,
      yscale: 1,
      opacity: 0.8,
      time: 400,
    },
    { z: defender.behind(5), scale: 0, xscale: 0, yscale: 0, time: 550 },
    'accel',
  );
};

/** PS `dualchop` → `xattack` template. */
const dualchop: MoveAnim = xattack;

/** PS `dualwingbeat` → `flight` template. */
const dualwingbeat: MoveAnim = flight;

/** PS `dynamicpunch` — a dark wash, three blooming fireballs, then a fist jab. */
const dynamicpunch: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.backgroundEffect('#000000', 700, 0.3);
  scene.showEffect(
    'fireball',
    { x: defender.x + 40, y: defender.y, z: defender.z, scale: 0, opacity: 0.6, time: 350 },
    { scale: 7, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x - 40, y: defender.y - 20, z: defender.z, scale: 0, opacity: 0.6, time: 500 },
    { scale: 7, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x + 10, y: defender.y + 20, z: defender.z, scale: 0, opacity: 0.6, time: 650 },
    { scale: 7, opacity: 0 },
    'decel',
  );
  punchattack(scene, actors);
};

/**
 * PS `earthpower` — the ground shudders and erupts with rock + fire bloom. PS shook
 * the field via `scene.$bg.animate({top,bottom}, …)` (no equivalent in our engine);
 * substituted with a brief brown ground-rumble `backgroundEffect` wash (same as
 * earthquake's substitution). The fireball/rock3 bursts + defender bob are verbatim.
 */
const earthpower: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  // Substitute for PS's $bg shake — a brief brown ground-rumble wash.
  scene.backgroundEffect('#8a6a3a', 1100, 0.18);

  scene.showEffect(
    'fireball',
    { x: defender.x + 10, y: defender.y - 35, z: defender.z, scale: 0, opacity: 0.8 },
    { scale: 3, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x + 10, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 0.6, time: 0 },
    { x: defender.x + 30, y: defender.y + 50, scale: 0.5, opacity: 0, time: 300 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x + 10, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 0.6, time: 100 },
    { x: defender.x - 10, y: defender.y + 50, scale: 0.5, opacity: 0, time: 400 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x + 40, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 0.6, time: 200 },
    { x: defender.x + 35, y: defender.y + 50, scale: 0.5, opacity: 0, time: 500 },
    'linear',
    'fade',
  );

  scene.showEffect(
    'fireball',
    { x: defender.x - 40, y: defender.y - 35, z: defender.z, scale: 0, opacity: 0.8, time: 150 },
    { scale: 3, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x - 40, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 0.6, time: 150 },
    { x: defender.x - 60, y: defender.y + 45, scale: 0.5, opacity: 0, time: 450 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x - 40, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 0.6, time: 250 },
    { x: defender.x - 20, y: defender.y + 45, scale: 0.5, opacity: 0, time: 550 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x - 40, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 0.6, time: 350 },
    { x: defender.x - 35, y: defender.y + 45, scale: 0.5, opacity: 0, time: 650 },
    'linear',
    'fade',
  );

  scene.showEffect(
    'fireball',
    { x: defender.x + 40, y: defender.y - 35, z: defender.z, scale: 0, opacity: 0.8, time: 300 },
    { scale: 3, opacity: 0 },
    'linear',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x + 40, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 0.6, time: 300 },
    { x: defender.x + 60, y: defender.y + 65, scale: 0.5, opacity: 0, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x + 40, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 0.6, time: 400 },
    { x: defender.x + 20, y: defender.y + 65, scale: 0.5, opacity: 0, time: 700 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'rock3',
    { x: defender.x + 40, y: defender.y - 35, z: defender.z, scale: 0.4, opacity: 0.6, time: 500 },
    { x: defender.x + 30, y: defender.y + 65, scale: 0.5, opacity: 0, time: 800 },
    'linear',
    'fade',
  );

  defender.delay(50);
  defender.anim({ y: defender.y - 10, time: 75 });
  defender.anim({ y: defender.y + 10, time: 100 });
  defender.anim({ y: defender.y - 7, time: 100 });
  defender.anim({ y: defender.y + 7, time: 100 });
  defender.anim({ y: defender.y - 7, time: 100 });
  defender.anim({ y: defender.y + 7, time: 100 });
  defender.anim({ y: defender.y - 2, time: 100 });
  defender.anim({ y: defender.y + 2, time: 100 });
  defender.anim({ y: defender.y, time: 100 });
};

/** PS `echoedvoice` → PS aliases this to `hypervoice` (not in our set); use the
 * vendored `sound` ripple template, the closest sonic stand-in. */
const echoedvoice: MoveAnim = sound;

/** PS `eerieimpulse` → `sound` template. */
const eerieimpulse: MoveAnim = sound;

/** PS `eggbomb` → PS aliases this to `magnetbomb` (not in our set); substitute the
 * generic projectile-lob, `fling` (a ballistic bomb toss), the closest match. */

/** PS `electricterrain` — a yellow field wash plus the user's dance. */
const electricterrain: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  scene.backgroundEffect('#FFFF00', 1000, 0.3);
  dance(scene, actors);
};

/** PS `electroball` — a single growing electro orb hurled at the defender. */
const electroball: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.3 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1, opacity: 0.6, time: 500 },
    'linear',
    'explode',
  );

  defender.delay(500);
  defender.anim({ z: defender.behind(5), time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `electroweb` — three sticky webs splatter across the defender. */
const electroweb: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'web',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'web',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 100 },
    { x: defender.x + 40, y: defender.y - 20, z: defender.z, scale: 0.5, opacity: 1, time: 500 },
    'ballistic',
    'explode',
  );
  scene.showEffect(
    'web',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0, time: 200 },
    { x: defender.x - 30, y: defender.y - 10, z: defender.z, scale: 0.5, opacity: 1, time: 600 },
    'ballistic',
    'explode',
  );
};

/** PS `embargo` — poison wisps swap sides over the defender twice. */
const embargo: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 400 },
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 400 },
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 600 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 600 },
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 800 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'poisonwisp',
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.2, opacity: 1, time: 600 },
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.4, opacity: 0.4, time: 800 },
    'linear',
    'fade',
  );
};

/** PS `encore` — a quick clap-shake of the user. */
const encore: MoveAnim = (_scene: FxScene, [attacker]: Actor[]) => {
  attacker.anim({ x: attacker.x - 10, time: 100 });
  attacker.anim({ x: attacker.x + 10, time: 200 });
  attacker.anim({ x: attacker.x, time: 100 });
};

/** PS `endeavor` — two shadow orbs converge on the defender, then an X-strike. */
const endeavor: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 450 },
    {
      x: defender.leftof(20),
      y: defender.y,
      z: defender.behind(20),
      scale: 0,
      opacity: 1,
      time: 750,
    },
    'linear',
    'fade',
  );
  scene.showEffect(
    'shadowball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 0, time: 750 },
    {
      x: defender.leftof(-20),
      y: defender.y,
      z: defender.behind(20),
      scale: 0,
      opacity: 1,
      time: 1050,
    },
    'linear',
    'fade',
  );
  xattack(scene, actors);
};

/** PS `endure` → `selfstatus` template. */
const endure: MoveAnim = selfstatus;

/** PS `energyball` — an energy orb + wisp burst, then scattering shards; defender rocks. */
const energyball: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'energyball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.8, opacity: 0.3 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1.5, time: 400 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x + 30, y: defender.y + 25, z: defender.z, scale: 0.2, opacity: 0.6, time: 400 },
    { x: defender.x + 50, y: defender.y + 10, opacity: 0.3, time: 700 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x - 30, y: defender.y - 20, z: defender.z, scale: 0.2, opacity: 0.6, time: 500 },
    { x: defender.x - 50, y: defender.y - 20, opacity: 0.3, time: 700 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x + 15, y: defender.y + 10, z: defender.z, scale: 0.2, opacity: 0.7, time: 600 },
    { x: defender.x + 35, y: defender.y + 30, opacity: 0.3, time: 800 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'energyball',
    { x: defender.x - 15, y: defender.y - 30, z: defender.z, scale: 0.2, opacity: 0.7, time: 600 },
    { x: defender.x - 35, y: defender.y - 40, opacity: 0.3, time: 800 },
    'accel',
    'explode',
  );

  defender.delay(400);
  defender.anim({ z: defender.behind(5), time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `entrainment` → PS aliases this to `painsplit` (not in our set); use the
 * vendored `selfstatus` pulse as the closest no-projectile stand-in. */
const entrainment: MoveAnim = selfstatus;

/**
 * PS `eruption` — a volcanic column erupts off the user and rains fire/rock on the
 * defender. PS handled multiple defenders (`[attacker, ...defenders]`); our 1v1 API
 * is `[attacker, defender]`, so the per-defender shudder loop collapses to the one
 * defender. The `linear-gradient(#390000 30%, #B84038)` wash (which our CSS parser
 * would render flat-white) is substituted with a flat dark-red `#5a1810`.
 */
const eruption: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  defender.delay(625);
  defender.anim({ x: defender.x - 30, time: 75 });
  defender.anim({ x: defender.x + 30, time: 100 });
  defender.anim({ x: defender.x - 30, time: 100 });
  defender.anim({ x: defender.x + 30, time: 100 });
  defender.anim({ x: defender.x, time: 100 });

  // Substitute for PS's linear-gradient(#390000 30%, #B84038) — flat dark red.
  scene.backgroundEffect('#5a1810', 1100, 0.4);
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 1 },
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 6, opacity: 0 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.4, opacity: 0.6, time: 0 },
    { x: attacker.x + 30, y: attacker.y + 150, z: attacker.z, scale: 0.5, opacity: 0, time: 300 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.4, opacity: 0.6, time: 100 },
    { x: attacker.x - 10, y: attacker.y + 150, z: attacker.z, scale: 0.5, opacity: 0, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'flareball',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.4, opacity: 0.6, time: 200 },
    { x: attacker.x + 35, y: attacker.y + 150, z: attacker.z, scale: 0.5, opacity: 0, time: 500 },
    'decel',
    'fade',
  );

  scene.showEffect(
    'rock3',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.4, opacity: 0.6, time: 0 },
    { x: attacker.x + 30, y: attacker.y + 150, z: attacker.z, scale: 0.5, opacity: 0, time: 300 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.9, opacity: 0.6, time: 50 },
    { x: attacker.x - 30, y: attacker.y + 150, z: attacker.z, scale: 1, opacity: 0, time: 350 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'rock3',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.4, opacity: 0.6, time: 100 },
    { x: attacker.x - 10, y: attacker.y + 150, z: attacker.z, scale: 0.5, opacity: 0, time: 400 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.9, opacity: 0.6, time: 150 },
    { x: attacker.x + 10, y: attacker.y + 150, z: attacker.z, scale: 1, opacity: 0, time: 450 },
    'decel',
    'fade',
  );
  scene.showEffect(
    'rock3',
    { x: attacker.x, y: attacker.y - 35, z: attacker.z, scale: 0.4, opacity: 0.6, time: 200 },
    { x: attacker.x + 35, y: attacker.y + 150, z: attacker.z, scale: 0.5, opacity: 0, time: 500 },
    'decel',
    'fade',
  );

  scene.showEffect(
    'fireball',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 300,
    },
    { x: defender.x + 45, y: defender.y + 20, z: defender.z, scale: 1.5, opacity: 0.7, time: 600 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'rock3',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 375,
    },
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 1, opacity: 0.7, time: 675 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'flareball',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 375,
    },
    { x: defender.x - 20, y: defender.y - 20, z: defender.z, scale: 1, opacity: 0.7, time: 675 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 450,
    },
    { x: defender.x + 30, y: defender.y, z: defender.z, scale: 1.5, opacity: 0.7, time: 750 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'rock3',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 525,
    },
    { x: defender.x + 40, y: defender.y + 5, z: defender.z, scale: 1, opacity: 0.7, time: 825 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'flareball',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 525,
    },
    { x: defender.x + 40, y: defender.y + 5, z: defender.z, scale: 1, opacity: 0.7, time: 825 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 575,
    },
    { x: defender.x - 70, y: defender.y + 5, z: defender.z, scale: 1.5, opacity: 0.7, time: 875 },
    'accel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    {
      x: attacker.x,
      y: attacker.y + 175,
      z: attacker.behind(-90),
      scale: 0.4,
      opacity: 1,
      time: 650,
    },
    { x: defender.x + 70, y: defender.y + 5, z: defender.z, scale: 1.5, opacity: 0.7, time: 950 },
    'accel',
    'explode',
  );
};

/** PS `explosion` — three blooming fireballs, then the user expands and vanishes. */
const explosion: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.showEffect(
    'fireball',
    { x: attacker.x + 40, y: attacker.y, z: attacker.z, scale: 0, opacity: 0.6 },
    { scale: 6, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x - 40, y: attacker.y - 20, z: attacker.z, scale: 0, opacity: 0.6, time: 150 },
    { scale: 6, opacity: 0 },
    'decel',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x + 10, y: attacker.y + 20, z: attacker.z, scale: 0, opacity: 0.6, time: 300 },
    { scale: 6, opacity: 0 },
    'decel',
  );
  // PS chained attacker.delay(450).anim(...); our Actor.delay is a soft no-op, so the
  // expand-and-fade lunge stands alone (the showEffect bursts carry the blast read).
  attacker.delay(450).anim({ scale: 4, time: 400, opacity: 0 }, 'linear');
};

/** PS `extremespeed` — a flickering hyper-dash with wisp trails + multi-impacts.
 * The two `showEffect(attacker.sp, …)` after-image streaks are OMITTED (no
 * sprite-as-effect in our engine; same as fastattack); the attacker's flicker
 * lunges below preserve the dash read. */
const extremespeed: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, scale: 1.5, opacity: 0.6, time: 600 },
    { x: attacker.x + 20, z: attacker.behind(-50), scale: 0.8, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y - 30, z: attacker.z, scale: 1.5, opacity: 0.6, time: 600 },
    { x: attacker.x - 20, z: attacker.behind(-50), scale: 0.8, opacity: 0, time: 1000 },
    'linear',
    'fade',
  );

  scene.showEffect(
    'impact',
    {
      x: defender.x - 25,
      y: defender.y,
      z: defender.behind(5),
      scale: 0.7,
      opacity: 0.7,
      time: 375,
    },
    { scale: 1.2, opacity: 0, time: 650 },
    'linear',
  );
  scene.showEffect(
    'impact',
    {
      x: defender.x + 25,
      y: defender.y - 5,
      z: defender.behind(5),
      scale: 0.7,
      opacity: 0.7,
      time: 500,
    },
    { scale: 1.2, opacity: 0, time: 775 },
    'linear',
  );
  scene.showEffect(
    'impact',
    {
      x: defender.x - 25,
      y: defender.y + 10,
      z: defender.behind(5),
      scale: 0.7,
      opacity: 0.7,
      time: 600,
    },
    { scale: 1, opacity: 0, time: 850 },
    'linear',
  );
  scene.showEffect(
    'impact',
    {
      x: defender.x + 2,
      y: defender.y + 5,
      z: defender.behind(5),
      scale: 1,
      opacity: 0.7,
      time: 600,
    },
    { scale: 1.5, opacity: 0, time: 975 },
    'linear',
  );

  // PS drew three attacker-sprite after-images here (showEffect(attacker.sp, …)) —
  // omitted (no sprite-as-effect in our engine). The flicker lunges convey the dash.

  attacker.anim({ opacity: 0, time: 50 }, 'linear');
  attacker.anim({ opacity: 1, time: 50 }, 'linear');
  attacker.anim({ opacity: 0, time: 50 }, 'linear');
  attacker.anim({ opacity: 1, time: 50 }, 'linear');
  attacker.anim({ opacity: 0, time: 50 }, 'linear');
  attacker.anim({ opacity: 1, time: 50 }, 'linear');
  attacker.anim({ opacity: 0, time: 50 }, 'linear');
  attacker.anim({ opacity: 1, time: 50 }, 'linear');
  attacker.anim({ z: attacker.behind(-100), opacity: 0, time: 75 }, 'accel');
  attacker.anim({ z: attacker.behind(70), opacity: 0, time: 1 }, 'linear');
  attacker.delay(600);
  attacker.anim({ opacity: 1, time: 500 }, 'decel');

  defender.delay(375);
  defender.anim({ x: defender.leftof(5), z: defender.behind(20), time: 50 });
  defender.anim({ time: 75 });
  defender.anim({ z: defender.behind(20), time: 75 });
  defender.anim({ time: 75 });
  defender.anim({ x: defender.leftof(10), z: defender.behind(30), time: 250 }, 'decel');
  defender.anim({ time: 350 }, 'swing');
};

/** PS `facade` → `xattack` template. */
const facade: MoveAnim = xattack;

/** PS `fakeout` — a vanish, a snapping double-chop, then a flinch. */
const fakeout: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'rightchop',
    {
      x: defender.x + 15,
      y: defender.y,
      z: defender.behind(-120),
      scale: 0.3,
      opacity: 0.1,
      time: 200,
    },
    {
      x: defender.x + 4,
      y: defender.y + 7,
      z: defender.behind(20),
      xscale: 0.15,
      yscale: 0.35,
      opacity: 1,
      time: 400,
    },
    'accel',
    'explode',
  );
  scene.showEffect(
    'leftchop',
    {
      x: defender.x - 15,
      y: defender.y,
      z: defender.behind(-120),
      scale: 0.3,
      opacity: 0.1,
      time: 200,
    },
    {
      x: defender.x - 4,
      y: defender.y + 7,
      z: defender.behind(20),
      xscale: 0.15,
      yscale: 0.35,
      opacity: 1,
      time: 400,
    },
    'accel',
    'explode',
  );

  attacker.anim(
    { x: attacker.leftof(-20), y: attacker.y, z: attacker.behind(-20), opacity: 0, time: 200 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-120), opacity: 0, time: 1 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(40), opacity: 1, time: 250 },
    'linear',
  );
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-5), opacity: 0, time: 300 },
    'linear',
  );
  attacker.anim({ opacity: 0, time: 1 }, 'linear');
  attacker.anim({ time: 300, opacity: 1 }, 'linear');
  defender.delay(370);
  defender.anim({ z: defender.behind(20), yscale: 1.4, xscale: 0.7, time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `faketears` → `dance` template. */
const faketears: MoveAnim = dance;

/** PS `falseswipe` → `slashattack` template. */
const falseswipe: MoveAnim = slashattack;

/** PS `featherdance` — the user dances and feathers drift down onto the defender. */
const featherdance: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [attacker, defender] = actors;
  dance(scene, actors);
  scene.showEffect(
    'feather',
    { x: attacker.x, y: attacker.y + 10, z: attacker.z, scale: 0.3, opacity: 1, time: 0 },
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.4, time: 600 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'feather',
    { x: attacker.x, y: attacker.y + 10, z: attacker.z, scale: 0.3, opacity: 1, time: 100 },
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.4, time: 700 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'feather',
    { x: attacker.x, y: attacker.y + 10, z: attacker.z, scale: 0.3, opacity: 1, time: 200 },
    { x: defender.x + 25, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.4, time: 800 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'feather',
    { x: attacker.x, y: attacker.y + 10, z: attacker.z, scale: 0.3, opacity: 1, time: 300 },
    { x: defender.x - 25, y: defender.y, z: defender.z, scale: 0.5, opacity: 0.4, time: 900 },
    'ballistic2Under',
    'fade',
  );

  scene.showEffect(
    'feather',
    { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 400 },
    { x: defender.x + 5, y: defender.y - 20, z: defender.z, scale: 0.1, opacity: 0.4, time: 700 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'feather',
    { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 500 },
    { x: defender.x - 10, y: defender.y - 20, z: defender.z, scale: 0.1, opacity: 0.4, time: 800 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'feather',
    { x: defender.x + 25, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 600 },
    { x: defender.x, y: defender.y - 20, z: defender.z, scale: 0.1, opacity: 0.4, time: 900 },
    'ballistic2Under',
    'fade',
  );
  scene.showEffect(
    'feather',
    { x: defender.x - 25, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 700 },
    { x: defender.x, y: defender.y - 20, z: defender.z, scale: 0.1, opacity: 0.4, time: 1000 },
    'ballistic2Under',
    'fade',
  );
};

/** PS `feint` → `sneakattack` template. */
const feint: MoveAnim = sneakattack;

/** PS `feintattack` → `sneakattack` template. */
const feintattack: MoveAnim = sneakattack;

/** PS `fellstinger` → PS aliases this to `bulletseed` (not in our set); substitute
 * the canonical contact lunge, `contactattack`. */
const fellstinger: MoveAnim = contactattack;

/** PS `finalgambit` — the user hurls itself bodily through the defender and fades.
 * The two `showEffect(attacker.sp, …)` body-streaks are OMITTED (no sprite-as-effect
 * in our engine); the wisp burst + the attacker's own charging lunge carry the read. */
const finalgambit: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.3, time: 500 },
    { scale: 4, opacity: 0, time: 800 },
    'linear',
  );
  // PS drew two attacker-sprite body-streaks here (showEffect(attacker.sp, …)) — omitted.
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim(
    { x: defender.x, y: defender.y, z: defender.behind(-5), scale: 5, opacity: 0, time: 300 },
    'linear',
  );
  attacker.anim({ opacity: 0, time: 1 }, 'linear');
  attacker.anim({ time: 300 }, 'linear');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `fireblast` — a dark flash, a red wash, a fireball burst that scatters; defender rocks.
 * The `linear-gradient(#390000 30%, #B84038)` wash is substituted with flat `#5a1810`. */
const fireblast: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#000000', 500, 0.7);
  // Substitute for PS's linear-gradient(#390000 30%, #B84038) — flat dark red.
  scene.backgroundEffect('#5a1810', 600, 0.4, 500);
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 1, time: 500 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.2, time: 50 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 1, time: 550 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 1, time: 500 },
    { x: defender.x, y: defender.y + 100, scale: 3, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 1, time: 500 },
    { x: defender.x - 60, y: defender.y - 80, scale: 3, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 1, time: 500 },
    { x: defender.x + 60, y: defender.y - 80, scale: 3, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 1, time: 500 },
    { x: defender.x - 90, y: defender.y + 40, scale: 3, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 2, opacity: 1, time: 500 },
    { x: defender.x + 90, y: defender.y + 40, scale: 3, opacity: 0, time: 1100 },
    'linear',
    'fade',
  );

  defender.delay(500);
  defender.anim({ z: defender.behind(10), time: 200 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `firefang` — two fireballs flare off the jaws, then a bite + a contact lunge. */
const firefang: MoveAnim = (scene: FxScene, actors: Actor[]) => {
  const [, defender] = actors;
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 450 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 550 },
    { x: defender.x, y: defender.y - 40, z: defender.behind(15), scale: 3, opacity: 0, time: 900 },
    'linear',
  );

  bite(scene, actors);
  contactattack(scene, actors);
};

/** PS `firespin` — four fireballs swirl across the defender in a vortex. */
const firespin: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'fireball',
      {
        x: defender.x + 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 0.5,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x - 50,
        y: defender.y,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'fireball',
      {
        x: defender.x - 50,
        y: defender.y + 35,
        z: defender.z,
        scale: 0.5,
        opacity: 1,
        time: 200 * i,
      },
      {
        x: defender.x + 50,
        y: defender.y,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'fireball',
      { x: defender.x + 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 200 * i },
      {
        x: defender.x - 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
    scene.showEffect(
      'fireball',
      { x: defender.x - 50, y: defender.y, z: defender.z, scale: 0.5, opacity: 1, time: 200 * i },
      {
        x: defender.x + 50,
        y: defender.y - 35,
        z: defender.z,
        scale: 1,
        opacity: 0.4,
        time: 200 * i + 200,
      },
      'linear',
      'fade',
    );
  }
};

/** PS `firstimpression` — a brown flash, electro sparks at both, then a quick strike. */
const firstimpression: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#987058', 600, 0.3, 400);
  scene.showEffect(
    'electroball',
    { x: attacker.x, y: attacker.y + 60, z: attacker.z, scale: 0.5, opacity: 1 },
    { scale: 0.25, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    {
      x: attacker.x,
      y: attacker.y + 100,
      z: attacker.z,
      scale: 0.5,
      xscale: 0.25,
      yscale: 0.75,
      opacity: 1,
    },
    { scale: 0.25, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    { x: defender.x, y: defender.y + 60, z: defender.z, scale: 0.5, opacity: 1 },
    { scale: 0.25, opacity: 0, time: 400 },
    'linear',
  );
  scene.showEffect(
    'electroball',
    {
      x: defender.x,
      y: defender.y + 100,
      z: defender.z,
      scale: 0.5,
      xscale: 0.25,
      yscale: 0.75,
      opacity: 1,
    },
    { scale: 0.25, opacity: 0, time: 400 },
    'linear',
  );

  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 500 },
    { scale: 3, opacity: 0, time: 800 },
    'linear',
  );
  scene.showEffect(
    'impact',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 0.5, time: 700 },
    { scale: 3, opacity: 0, time: 1000 },
    'linear',
  );

  attacker.anim({ y: attacker.y + 5, xscale: 0.9, yscale: 1.1, time: 100 }, 'swing');
  attacker.anim({ time: 100 }, 'swing');
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.anim({ y: defender.y + 5, xscale: 0.9, yscale: 1.1, time: 100 }, 'swing');
  defender.anim({ time: 100 }, 'swing');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `fissure` → alias of `earthquake`. Not in our ported set; substitute the
 * closest vendored stand-in, `earthpower` (a ground-rumble + rock eruption). */
const fissure: MoveAnim = earthpower;

/** PS `flail` → `xattack` template. */
const flail: MoveAnim = xattack;

/** PS `flamecharge` — fireballs spark off the user, then a flaming charge into the defender. */
const flamecharge: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 0 },
    { x: attacker.x - 25, y: attacker.y - 25, scale: 2, opacity: 0, time: 300 },
    'ballistic',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 150 },
    { x: attacker.x + 30, y: attacker.y - 20, scale: 2, opacity: 0, time: 450 },
    'ballistic',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 250 },
    { x: attacker.x + 5, y: attacker.y - 40, scale: 2, opacity: 0, time: 550 },
    'ballistic',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0, opacity: 1, time: 300 },
    { x: attacker.x - 20, y: attacker.y - 20, scale: 2, opacity: 0, time: 600 },
    'ballistic',
  );

  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 600 },
    { scale: 5, opacity: 0, time: 900 },
    'linear',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 700 },
    { scale: 8, opacity: 0, time: 1000 },
    'linear',
  );
  attacker.delay(300);
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(580);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `flamewheel` → alias of `flamecharge`. */
const flamewheel: MoveAnim = flamecharge;

/** PS `flareblitz` — a fiery wash, two fireball blooms, then a blazing tackle.
 * The `linear-gradient(#390000 30%, #B84038)` wash is substituted with flat `#5a1810`. */
const flareblitz: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  // Substitute for PS's linear-gradient(#390000 30%, #B84038) — flat dark red.
  scene.backgroundEffect('#5a1810', 600, 0.6);
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 300 },
    { scale: 8, opacity: 0, time: 600 },
    'linear',
  );
  scene.showEffect(
    'fireball',
    { x: defender.x, y: defender.y, z: defender.z, scale: 0, opacity: 1, time: 500 },
    { scale: 8, opacity: 0, time: 800 },
    'linear',
  );
  attacker.anim({ x: defender.x, y: defender.y, z: defender.behind(-5), time: 300 }, 'accel');
  attacker.anim({ time: 500 }, 'ballistic2Back');
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');
};

/** PS `flash` — a white flash + an expanding wisp bloom off the user. */
const flash: MoveAnim = (scene: FxScene, [attacker]: Actor[]) => {
  scene.backgroundEffect('#ffffff', 600, 0.6);
  scene.showEffect(
    'wisp',
    { x: attacker.leftof(-10), y: attacker.y + 10, z: attacker.z, scale: 0.1, opacity: 0.8 },
    { scale: 15, opacity: 0.8, time: 500 },
    'linear',
    'fade',
  );
};

/** PS `flashcannon` — wisp + water-wisp orbs converge and burst on the defender. */
const flashcannon: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 50 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 250 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'waterwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 100 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 150 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 350 },
    'linear',
    'explode',
  );
};

/** PS `fling` — a single shadow orb lobbed at the defender and bursting. */
const fling: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'shadowball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 0 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 0.4, opacity: 1, time: 400 },
    'ballistic',
    'explode',
  );
};

/** PS `flatter` → PS aliases this to `attract` (not in our set); use the vendored
 * `dance` sway as the closest no-projectile stand-in. */
const flatter: MoveAnim = dance;

/** PS `eggbomb` → alias of `magnetbomb` (not in our set); substitute `fling`,
 * a ballistic single-bomb lob. (Declared after `fling` so it can reference it.) */
const eggbomb: MoveAnim = fling;

/**
 * PS `ember` — a single fireball travels to the defender and bursts. (Our base
 * SHOWDOWN_MOVE_FLAVOR mapped this to a generic fireball projectile; ported here as
 * an equivalent explicit fireball travel-and-explode.)
 */
const ember: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.1, opacity: 1 },
    { x: defender.x, y: defender.y, z: defender.behind(40), scale: 1, opacity: 0.5 },
    'linear',
    'explode',
  );
};

/** PS `extrasensory` → alias of `psychic` — a purple double-wash; the defender
 * pulses larger and smaller. Ported verbatim from PS `psychic`. */
const extrasensory: MoveAnim = (scene: FxScene, [, defender]: Actor[]) => {
  scene.backgroundEffect('#AA44BB', 250, 0.6);
  scene.backgroundEffect('#AA44FF', 250, 0.6, 400);
  defender.anim({ scale: 1.2, time: 100 });
  defender.anim({ scale: 1, time: 100 });
  defender.anim({ scale: 1.4, time: 150 });
  defender.anim({ scale: 1, time: 150 });
  scene.wait(700);
};

/** PS `expandingforce` → alias of `psybeam` — alternating mist/poison orbs converge
 * and burst on the defender. Ported verbatim from PS `psybeam`. */
const expandingforce: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2 },
    { x: defender.x, y: defender.y, z: defender.behind(20), opacity: 0.6, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 50 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 250 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'mistball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 100 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(20), opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'poisonwisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.5, opacity: 0.2, time: 150 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(20), opacity: 0.6, time: 350 },
    'linear',
    'explode',
  );
};

/** PS `fairywind` → alias of `dazzlinggleam` — a pink wash and a fan of wisps that
 * sparkle across the defender. Ported verbatim from PS `dazzlinggleam`. */
const fairywind: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.backgroundEffect('#FF99FF', 700, 0.5);
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6 },
    { x: defender.x + 30, y: defender.y + 30, z: defender.z, scale: 1, opacity: 0.3, time: 200 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 75 },
    { x: defender.x + 20, y: defender.y - 30, z: defender.z, scale: 1, opacity: 0.3, time: 275 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 150 },
    { x: defender.x - 30, y: defender.y, z: defender.z, scale: 1, opacity: 0.3, time: 350 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 225 },
    { x: defender.x - 10, y: defender.y + 10, z: defender.z, scale: 1, opacity: 0.3, time: 425 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 300 },
    { x: defender.x + 10, y: defender.y - 10, z: defender.z, scale: 1, opacity: 0.3, time: 500 },
    'linear',
    'explode',
  );
  scene.showEffect(
    'wisp',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 0.6, opacity: 0.6, time: 375 },
    { x: defender.x - 20, y: defender.y, z: defender.z, scale: 1, opacity: 0.3, time: 575 },
    'linear',
    'explode',
  );
};

/** PS `firepledge` → alias of `flamethrower` — four fireballs spray onto the defender
 * and burst. Ported verbatim from PS `flamethrower`. */
const firepledge: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7 },
    { x: defender.x, y: defender.y, z: defender.z, opacity: 0.6, time: 400 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 100 },
    { x: defender.x + 10, y: defender.y - 5, z: defender.behind(0), opacity: 0.6, time: 500 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 200 },
    { x: defender.x - 10, y: defender.y + 5, z: defender.behind(0), opacity: 0.6, time: 600 },
    'decel',
    'explode',
  );
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7, time: 300 },
    { x: defender.x, y: defender.y - 5, z: defender.behind(0), opacity: 0.6, time: 700 },
    'decel',
    'explode',
  );
};

/**
 * PS `flameburst` → alias of `shelltrap` — a dark wash, fans of flares burst outward,
 * then a fireball detonates on the defender. PS handled multiple defenders
 * (`[attacker, ...defenders]`); our 1v1 API collapses the per-defender shudder loop to
 * the single defender. (We do not port `shelltrap`'s `prepareAnim` charge, since
 * flameburst is not a charge move.)
 */
const flameburst: MoveAnim = (scene: FxScene, [attacker, defender]: Actor[]) => {
  defender.delay(280);
  defender.anim({ z: defender.behind(20), time: 100 }, 'swing');
  defender.anim({ time: 300 }, 'swing');

  const xf = [1, -1, 1, -1];
  const yf = [1, -1, -1, 1];
  const xf2 = [1, 0, -1, 0];
  const yf2 = [0, 1, 0, -1];

  scene.backgroundEffect('#000000', 600, 0.3);

  for (let i = 0; i < 4; i++) {
    scene.showEffect(
      'flareball',
      { x: attacker.x, y: attacker.y, z: attacker.behind(-5), scale: 0.5, opacity: 1 },
      {
        x: defender.x + 240 * xf[i],
        y: defender.y,
        z: defender.z + 137 * yf[i],
        opacity: 0,
        time: 600,
      },
      'decel',
    );
    scene.showEffect(
      'flareball',
      { x: attacker.x, y: attacker.y, z: attacker.behind(-5), scale: 0.5, opacity: 1 },
      {
        x: defender.x + 339 * xf2[i],
        y: defender.y + 10,
        z: defender.z + 194 * yf2[i],
        scale: 1,
        opacity: 0,
        time: 600,
      },
      'decel',
    );
  }
  scene.showEffect(
    'fireball',
    { x: attacker.x, y: attacker.y, z: attacker.z, scale: 1, opacity: 0.7 },
    { x: defender.x, y: defender.y, z: defender.z, scale: 1.5, opacity: 0.6, time: 300 },
    'linear',
    'explode',
  );
};

/* ──────────────────────────────────────────────────────────────────────────
 * The batch-2 table, keyed by Showdown move id (toID form).
 * ──────────────────────────────────────────────────────────────────────── */

export const SHOWDOWN_BATCH_2: Record<string, MoveAnim> = {
  dragonbreath,
  dragoncheer,
  dragonclaw,
  dragondance,
  dragonhammer,
  dragonpulse,
  dragonrage,
  dragonrush,
  dragontail,
  drainpunch,
  drainingkiss,
  dreameater,
  drillpeck,
  drillrun,
  dualchop,
  dualwingbeat,
  dynamicpunch,
  earthpower,
  echoedvoice,
  eerieimpulse,
  eggbomb,
  electricterrain,
  electroball,
  electroweb,
  embargo,
  ember,
  encore,
  endeavor,
  endure,
  energyball,
  entrainment,
  eruption,
  expandingforce,
  explosion,
  extrasensory,
  extremespeed,
  facade,
  fairywind,
  fakeout,
  faketears,
  falseswipe,
  featherdance,
  feint,
  feintattack,
  fellstinger,
  finalgambit,
  fireblast,
  firefang,
  firepledge,
  firespin,
  firstimpression,
  fissure,
  flail,
  flameburst,
  flamecharge,
  flamewheel,
  flareblitz,
  flash,
  flashcannon,
  flatter,
  fling,
};
